import { useEffect, useLayoutEffect, useRef } from "react";
import type { IModalProps } from "./types";
import { CloseButton, ModalBox, ModalOverlay } from "./styles";
import XIcon from "../../assets/icons/XIcon";

export const ModalComponent: React.FC<IModalProps> = ({
  children,
  closeModal,
  isWhite,
  hidden,
  preventClose,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  // Read inside the two listeners below instead of adding `hidden` to their
  // effect deps — both attach once on mount (`[]`), and every other one of
  // this component's 14 consumers never passes `hidden` at all, so keeping
  // those effects untouched keeps this change a no-op for them.
  //
  // `useLayoutEffect`, not `useEffect`: React paints `visibility: hidden`
  // during the commit phase, but a passive effect only runs after paint —
  // a `mousedown` landing in that gap would still read `hiddenRef.current
  // === false` and close the modal on the very frame something (a
  // portaled viewer, say) opens on top of it. A layout effect runs before
  // paint, closing the gap at zero cost. In practice this modal's own
  // outside-click listener already registers before a child's would (the
  // parent mounts first, and `mousedown` leads `click`), so the race is
  // theoretical today — but that ordering is incidental to render order,
  // not guaranteed by anything, which is exactly what makes it worth
  // closing for free rather than trusting it.
  const hiddenRef = useRef(hidden);
  useLayoutEffect(() => {
    hiddenRef.current = hidden;
  }, [hidden]);

  // 017-request-detail-modal-tabs, finding 2 (AC-40). Same ref idiom as
  // `hiddenRef` above, read inside the window-level listener instead of
  // added to its effect deps, for the same reason: that effect attaches
  // once (`[]`), and every consumer that never passes `preventClose` sees
  // no change. Unlike `hiddenRef`, this doesn't gate visibility — it gates
  // whether `Escape` (below) and `CloseButton` (render, below) are allowed
  // to call `closeModal` at all.
  const preventCloseRef = useRef(preventClose);
  useLayoutEffect(() => {
    preventCloseRef.current = preventClose;
  }, [preventClose]);

  useEffect(() => {
    const handleOutclick = (event: MouseEvent) => {
      if (hiddenRef.current) return;
      if (preventCloseRef.current) return;
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        closeModal
      ) {
        closeModal();
      }
    };

    window.addEventListener("mousedown", handleOutclick);

    return () => {
      window.removeEventListener("mousedown", handleOutclick);
    };
  }, []);

  useEffect(() => {
    const handleKeydown = (event: any) => {
      if (hiddenRef.current) return;
      if (preventCloseRef.current) return;
      if (event.key === "Escape" && closeModal) {
        closeModal();
      }
    };

    window.addEventListener("keydown", handleKeydown);

    return () => {
      window.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <ModalOverlay $hidden={hidden}>
      <ModalBox ref={modalRef}>
        {closeModal ? (
          <CloseButton
            isWhite={isWhite}
            data-testid="modal-close-button"
            onClick={() => {
              if (preventClose) return;
              closeModal();
            }}
          >
            <XIcon />
          </CloseButton>
        ) : null}
        {children}
      </ModalBox>
    </ModalOverlay>
  );
};
